import { t } from 'src/i18n';
import { isEmpty } from 'lodash';
import actions from './actions';
import { createReducer } from '../../../helpers/reducer';
import { keyBy } from '../../../helpers/core';
import {
  PRE_ENROLMENT_HARD_LIMIT_FIELD,
  PRE_ENROLMENT_HARD_LIMIT_MINIMUM_MINUTES,
  PRE_ENROLMENT_INACTIVITY_FIELD,
  PRE_ENROLMENT_INACTIVITY_MINIMUM_DAYS,
  PRE_ENROLMENT_INACTIVITY_MAXIMUM_DAYS,
} from './automationConfig.const';

const initState = {
  editForm: null,
  loading: false,
  formInfo: undefined,
  paymentSettingsId: undefined,
};

function validate(attr, value, isInitial = false) {
  let error = '';

  if (attr === 'enabled_payment_methods') {
    if (isEmpty(value)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.admin.automationConfig.payment_methods.enabled_payment_methods_label'),
      });
    }
  }

  if (attr === PRE_ENROLMENT_HARD_LIMIT_FIELD) {
    const minutes = parseInt(value, 10);

    if (!Number.isFinite(minutes)) {
      error = t('dash.validation.required_error', {
        fieldName: t('dash.admin.automationConfig.pre_enrolment_hard_limit.title'),
      });
    } else if (minutes < PRE_ENROLMENT_HARD_LIMIT_MINIMUM_MINUTES) {
      error = t('dash.admin.automationConfig.pre_enrolment_hard_limit.minimum_error', {
        minimum: PRE_ENROLMENT_HARD_LIMIT_MINIMUM_MINUTES,
      });
    }
  }

  if (attr === PRE_ENROLMENT_INACTIVITY_FIELD && !isInitial && value !== '' && value != null) {
    const days = Number(value);

    if (!Number.isInteger(days)) {
      error = t('dash.admin.automationConfig.pre_enrolment_inactivity_period.whole_number_error');
    } else if (days < PRE_ENROLMENT_INACTIVITY_MINIMUM_DAYS) {
      error = t('dash.admin.automationConfig.pre_enrolment_inactivity_period.minimum_error', {
        minimum: PRE_ENROLMENT_INACTIVITY_MINIMUM_DAYS,
      });
    } else if (days > PRE_ENROLMENT_INACTIVITY_MAXIMUM_DAYS) {
      error = t('dash.admin.automationConfig.pre_enrolment_inactivity_period.maximum_error', {
        maximum: PRE_ENROLMENT_INACTIVITY_MAXIMUM_DAYS,
      });
    }
  }

  return error;
}

function onLoad(state) {
  return {
    ...state,
    loading: true,
  };
}

function onLoadSuccess(state, { payload }) {
  const { data } = payload;
  const { payment_settings, ...restFields } = data;
  const propsArray = Object.keys(restFields);

  const editForm = keyBy(
    propsArray,
    prop => prop,
    prop => ({
      name: prop,
      value: data[prop],
      error: validate(prop, data[prop], true),
      touched: false,
    }),
  );

  const { id: paymentSettingsId, default_payment_method, enabled_payment_methods } = payment_settings;

  editForm.default_payment_method = {
    name: 'default_payment_method',
    value: default_payment_method.id,
    error: validate('default_payment_method', default_payment_method.id),
    touched: false,
  };

  editForm.enabled_payment_methods = {
    name: 'enabled_payment_methods',
    value: enabled_payment_methods.map(({ id }) => id),
    error: validate(
      'enabled_payment_methods',
      enabled_payment_methods.map(({ id }) => id),
    ),
    touched: false,
  };

  return {
    ...state,
    loading: false,
    editForm,
    paymentSettingsId,
  };
}

function onLoadFormDataRequest(state) {
  return {
    ...state,
    loading: true,
  };
}

function onLoadFormDataSuccess(state, { payload }) {
  const { data } = payload;

  return {
    ...state,
    loading: false,
    formInfo: data,
  };
}

function onLoadFormDataError(state) {
  return {
    ...state,
    loading: false,
  };
}

function onClearStore() {
  return { ...initState };
}

function onChangeProperty(state, { payload }) {
  const { attr, value } = payload;
  const { editForm } = state;

  if (editForm[attr] && editForm[attr].value !== value) {
    return {
      ...state,
      editForm: {
        ...editForm,
        [attr]: {
          value,
          error: validate(attr, value),
          name: attr,
          touched: true,
        },
      },
    };
  }

  return state;
}

function onChangeNestedProperty(state, { payload }) {
  const { attrs, value } = payload;
  const editForm = { ...state.editForm };

  if (attrs.length > 2) {
    let data = null;
    const name = attrs[attrs.length - 1];

    for (let i = 0; i < attrs.length - 1; i += 1) {
      data = (data || editForm)[attrs[i]];
    }

    data[name] = value;
  } else if (attrs.length === 2) {
    if (editForm[attrs[0]] === undefined) {
      editForm[attrs[0]] = {};
    }
    editForm[attrs[0]][attrs[1]] = value;
  } else {
    editForm[attrs[0]] = value;
  }

  return { ...state, editForm };
}

function onChangePriorityPricingProperty(state, { payload }) {
  const { id, attr, value } = payload;
  const { editForm } = state;
  const priorityPricing = editForm.priority_pricings.value.find(item => item.id === id);

  if ((priorityPricing[attr] || priorityPricing[attr] === 0) && priorityPricing[attr] !== value) {
    priorityPricing[attr] = value;

    return {
      ...state,
      editForm: {
        ...editForm,
        priority_pricings: {
          ...editForm.priority_pricings,
        },
      },
    };
  }

  return state;
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

export default createReducer(initState, {
  [actions.LOAD]: onLoad,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CHANGE_NESTED_PROPERTY]: onChangeNestedProperty,
  [actions.CHANGE_PRIORITY_PRICING_PROPERTY]: onChangePriorityPricingProperty,
  [actions.CANCEL]: onCancel,
  [actions.TOUCH]: onTouch,
  [actions.LOAD_FORM_DATA_REQUEST]: onLoadFormDataRequest,
  [actions.LOAD_FORM_DATA_SUCCESS]: onLoadFormDataSuccess,
  [actions.LOAD_FORM_DATA_ERROR]: onLoadFormDataError,
});
